import { Injectable, signal } from '@angular/core';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaInstallHistory {
  totalEngagedMs: number;
  nextShowAtMs: number;
  installed: boolean;
}

const STORAGE_KEY = 'pwa_install_prompt';

const MINUTE = 60 * 1000;
const WORK_MS = 10 * MINUTE;

const EMPTY_HISTORY: PwaInstallHistory = {
  totalEngagedMs: 0,
  nextShowAtMs: WORK_MS,
  installed: false,
};

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  readonly canShow = signal(false);
  private _prompt: BeforeInstallPromptEvent | null = null;
  private _history = EMPTY_HISTORY;
  private _tickTimer: ReturnType<typeof setInterval> | null = null;
  private _lastTick = 0;

  constructor() {
    if (self.matchMedia('(display-mode: standalone)').matches) return;

    this._history = this._readHistory();

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this._prompt = e as BeforeInstallPromptEvent;

      if (this._history.installed) {
        this._writeHistory({ installed: false });
        this._resume();
      }

      this._maybeShow();
    });

    window.addEventListener('appinstalled', () => {
      this._stopTracking();
      this.canShow.set(false);
      this._writeHistory({ installed: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this._pause();
      else this._resume();
    });
    window.addEventListener('pagehide', () => this._pause());

    if (!this._history.installed) this._resume();
  }

  async install(): Promise<void> {
    if (!this._prompt) return;
    this.canShow.set(false);

    await this._prompt.prompt();
    const { outcome } = await this._prompt.userChoice;
    this._prompt = null;

    if (outcome === 'accepted') {
      this._stopTracking();
      this._writeHistory({ installed: true });
      return;
    }

    this._refuse();
  }

  snooze(): void {
    this.canShow.set(false);
    this._refuse();
  }

  private _refuse(): void {
    if (this._history.installed) return;
    this._persistTick();
    this._writeHistory({
      nextShowAtMs: this._history.totalEngagedMs + WORK_MS,
    });
  }

  private _maybeShow(): void {
    if (this._history.installed) return;
    if (!this._prompt || this.canShow()) return;
    if (this._history.totalEngagedMs < this._history.nextShowAtMs) return;
    this.canShow.set(true);
  }

  private _resume(): void {
    if (this._history.installed) return;
    if (this._tickTimer || document.hidden) return;
    this._lastTick = Date.now();
    this._tickTimer = setInterval(() => this._flush(), 1000);
  }

  private _pause(): void {
    if (!this._history.installed) this._flush();
    this._stopTracking();
  }

  private _stopTracking(): void {
    if (this._tickTimer) {
      clearInterval(this._tickTimer);
      this._tickTimer = null;
    }
    this._lastTick = 0;
  }

  private _flush(): void {
    if (this._history.installed) return;
    this._persistTick();
    this._maybeShow();
  }

  private _persistTick(): void {
    if (this._history.installed || !this._lastTick) return;

    const now = Date.now();
    const delta = document.hidden ? 0 : now - this._lastTick;
    this._lastTick = now;

    if (delta > 0) {
      this._writeHistory({
        totalEngagedMs: this._history.totalEngagedMs + delta,
      });
    }
  }

  private _readHistory(): PwaInstallHistory {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...EMPTY_HISTORY };
      const parsed = JSON.parse(raw) as Partial<PwaInstallHistory>;
      return {
        totalEngagedMs: parsed.totalEngagedMs ?? 0,
        nextShowAtMs: parsed.nextShowAtMs ?? WORK_MS,
        installed: parsed.installed ?? false,
      };
    } catch {
      return { ...EMPTY_HISTORY };
    }
  }

  private _writeHistory(patch: Partial<PwaInstallHistory>): void {
    this._history = { ...this._history, ...patch };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._history));
    } catch {
      return;
    }
  }
}
